import React, { useState, useEffect } from 'react';
import { 
  BarChart3, 
  Filter, 
  Download, 
  Eye,
  Clock,
  CheckCircle,
  TrendingUp,
  Database,
  Calendar,
  Play,
  FileText,
  Star,
  AlertCircle
} from 'lucide-react';
import { versionsApi, testsApi } from '../services/api';
import { AUTVersion, FormattedEvaluation, TestStatistics, TestExecution } from '../types';

interface ResultsProps {
  addNotification: (message: string, type: 'success' | 'error' | 'info' | 'warning') => void;
}

const Results: React.FC<ResultsProps> = ({ addNotification }) => {
  const [versions, setVersions] = useState<AUTVersion[]>([]);
  const [evaluations, setEvaluations] = useState<FormattedEvaluation[]>([]);
  const [testExecutions, setTestExecutions] = useState<TestExecution[]>([]);
  const [statistics, setStatistics] = useState<TestStatistics | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedVersion, setSelectedVersion] = useState<number>(0);
  const [showFilters, setShowFilters] = useState(false);
  const [scoreFilter, setScoreFilter] = useState<string>('all');
  const [activeTab, setActiveTab] = useState<'evaluations' | 'executions'>('evaluations');
  const [selectedEvaluation, setSelectedEvaluation] = useState<FormattedEvaluation | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  useEffect(() => {
    loadData();
    const handler = () => loadData();
    window.addEventListener('organizationChanged', handler);
    return () => window.removeEventListener('organizationChanged', handler);
  }, [selectedVersion]);

  const loadData = async () => {
    try {
      setLoading(true);
      const orgIdStr = localStorage.getItem('selectedOrganizationId');
      const projectIdStr = localStorage.getItem('selectedProjectId');
      const orgId = orgIdStr ? parseInt(orgIdStr) : 1;
      const projectId = projectIdStr ? parseInt(projectIdStr) : undefined;
      
      // Check if organization and project are selected
      if (!orgIdStr || !projectIdStr) {
        setVersions([]);
        setEvaluations([]);
        setTestExecutions([]);
        setStatistics(null);
        setLoading(false);
        return;
      }
      
      const [versionsData, evaluationsData, executionsData, statsData] = await Promise.all([
        projectId ? versionsApi.list(orgId, projectId) : Promise.resolve([] as AUTVersion[]),
        testsApi.listEvaluations(orgId, projectId, selectedVersion, 'excel'), // Only Excel evaluations
        testsApi.listExecutions(orgId, projectId, selectedVersion, undefined),
        testsApi.getStatistics(orgId, projectId, selectedVersion)
      ]);
      
      setVersions(versionsData);
      setEvaluations(evaluationsData || []);
      setTestExecutions(executionsData || []);
      setStatistics(statsData);
      
      // Debug logging
      console.log('Debug - Statistics data:', statsData);
      console.log('Debug - Evaluations data:', evaluationsData);
      console.log('Debug - Test executions data:', executionsData);
    } catch (error) {
      console.error('Error loading results data:', error);
      // Set empty data on error instead of showing error notification
      setEvaluations([]);
      setTestExecutions([]);
      setStatistics(null);
    } finally {
      setLoading(false);
    }
  };

  const normalizeScore = (score?: number | null): number | null => {
    return typeof score === 'number' && Number.isFinite(score) ? score : null;
  };

  const formatScore = (score?: number | null) => {
    const normalized = normalizeScore(score);
    return normalized === null ? 'N/A' : normalized.toFixed(2);
  };

  const getScoreColor = (score?: number | null) => {
    const normalized = normalizeScore(score);
    if (normalized === null) return 'text-slate-300 bg-slate-700';
    if (normalized >= 0.8) return 'text-green-200 bg-green-900/50';
    if (normalized >= 0.6) return 'text-yellow-200 bg-yellow-900/50';
    return 'text-red-200 bg-red-900/50';
  };

  const getScoreLabel = (score?: number | null) => {
    const normalized = normalizeScore(score);
    if (normalized === null) return 'No Score';
    if (normalized >= 0.8) return 'Excellent';
    if (normalized >= 0.6) return 'Good';
    if (normalized >= 0.4) return 'Fair';
    return 'Poor';
  };

  const getScoreTextColor = (score?: number | null) => {
    const normalized = normalizeScore(score);
    if (normalized === null) return 'text-slate-400';
    return normalized >= 0.8 ? 'text-green-400' : normalized >= 0.6 ? 'text-yellow-400' : 'text-red-400';
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleViewDetails = (evaluation: FormattedEvaluation) => {
    setSelectedEvaluation(evaluation);
    setShowDetailsModal(true);
  };

  const closeDetailsModal = () => {
    setShowDetailsModal(false);
    setSelectedEvaluation(null);
  };

  const filteredEvaluations = evaluations.filter(evaluation => {
    const score = normalizeScore(evaluation.overall_score);
    if (scoreFilter === 'all') return true;
    if (score === null) return false;
    if (scoreFilter === 'excellent' && score >= 0.8) return true;
    if (scoreFilter === 'good' && score >= 0.6 && score < 0.8) return true;
    if (scoreFilter === 'fair' && score >= 0.4 && score < 0.6) return true;
    if (scoreFilter === 'poor' && score < 0.4) return true;
    return false;
  });

  const exportResults = () => {
    if (activeTab === 'evaluations') {
      const csvContent = [
        ['Run ID', 'Question', 'Answer', 'Metric Type', 'Score', 'Row Number', 'Created At'],
        ...filteredEvaluations.map(evaluation => [
          evaluation.evaluation_run_id?.toString() || 'N/A',
          evaluation.question || evaluation.test_case,
          evaluation.answer || 'N/A',
          evaluation.metric_type?.replace('_', ' ').toUpperCase() || 'N/A',
          formatScore(evaluation.overall_score),
          evaluation.row_number?.toString() || 'N/A',
          formatDate(evaluation.created_at)
        ])
      ].map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `excel-evaluations-${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } else {
      const csvContent = [
        ['Test Case', 'Version', 'Response', 'Execution Time (s)', 'Tokens Used', 'Date'],
        ...testExecutions.map(execution => [
          execution.test_case?.question_text || 'N/A',
          execution.aut_version?.version_name || 'N/A',
          execution.customer_support_response.substring(0, 100) + '...',
          execution.execution_time.toString(),
          execution.tokens_used?.toString() || 'N/A',
          formatDate(execution.created_at)
        ])
      ].map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `test-executions-${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    }
    
    addNotification('Results exported successfully', 'success');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  // Check if organization and project are selected
  const orgIdStr = localStorage.getItem('selectedOrganizationId');
  const projectIdStr = localStorage.getItem('selectedProjectId');
  
  if (!orgIdStr || !projectIdStr) {
    return (
      <div className="space-y-6">
        <div className="bg-slate-800 rounded-lg shadow-sm border border-slate-700 p-6">
          <h1 className="text-2xl font-bold text-slate-100">Test Results & Evaluations</h1>
          <p className="text-slate-300 mt-2">Analyze test performance and AI evaluations</p>
        </div>
        <div className="bg-yellow-900/30 border border-yellow-600 rounded-lg p-8 text-center">
          <AlertCircle className="h-12 w-12 mx-auto mb-3 text-yellow-400" />
          <h3 className="text-lg font-medium text-slate-100 mb-2">No Organization or Project Selected</h3>
          <p className="text-slate-300">
            Please select an organization, project, and version from the Organizations page to view results and evaluations.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="bg-slate-800 rounded-lg shadow-sm border border-slate-700 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-100">Test Results & Evaluations</h1>
            <p className="text-slate-300 mt-2">Analyze test performance and AI evaluations</p>
          </div>
          <button
            onClick={exportResults}
            disabled={evaluations.length === 0 && testExecutions.length === 0}
            className="inline-flex items-center px-4 py-2 border border-slate-600 text-sm font-medium rounded-md text-slate-200 bg-slate-800 hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Download className="h-4 w-4 mr-2" />
            Export {activeTab === 'evaluations' ? 'Excel Evaluations' : 'Executions'}
          </button>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="bg-slate-800 rounded-lg shadow-sm border border-slate-700">
        <div className="border-b border-slate-700">
          <nav className="-mb-px flex space-x-8 px-6">
            <button
              onClick={() => setActiveTab('evaluations')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'evaluations'
                  ? 'border-purple-500 text-purple-400'
                  : 'border-transparent text-slate-400 hover:text-slate-200 hover:border-slate-500'
              }`}
            >
              <div className="flex items-center">
                <Star className="h-4 w-4 mr-2" />
                Excel Evaluations ({evaluations.length})
              </div>
            </button>
            <button
              onClick={() => setActiveTab('executions')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'executions'
                  ? 'border-purple-500 text-purple-400'
                  : 'border-transparent text-slate-400 hover:text-slate-200 hover:border-slate-500'
              }`}
            >
              <div className="flex items-center">
                <Play className="h-4 w-4 mr-2" />
                Test Executions ({testExecutions.length})
              </div>
            </button>
          </nav>
        </div>
      </div>

      {/* Statistics Overview */}
      {statistics && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left Side - Total Evaluations and Average Score */}
          <div className="space-y-6">
            {/* Total Evaluations */}
            <div className="bg-slate-800 rounded-lg shadow-sm border border-slate-700 p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <BarChart3 className="h-8 w-8 text-indigo-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-slate-400">Total Evaluations</p>
                  <p className="text-2xl font-semibold text-slate-100">
                    {statistics.evaluations.total_evaluations}
                  </p>
                  {statistics.evaluations.traditional && statistics.evaluations.excel && (
                    <div className="text-xs text-slate-400 mt-1">
                      Traditional: {statistics.evaluations.traditional.total_evaluations} | 
                      Excel: {statistics.evaluations.excel.total_evaluations}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Average Score */}
            <div className="bg-slate-800 rounded-lg shadow-sm border border-slate-700 p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <CheckCircle className="h-8 w-8 text-yellow-400" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-slate-400">Average Score</p>
                  <p className="text-2xl font-semibold text-slate-100">
                    {formatScore(statistics.evaluations.average_overall_score)}
                  </p>
                  <div className="text-xs text-slate-400 mt-1">
                    {getScoreLabel(statistics.evaluations.average_overall_score)}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Side - Metrics Breakdown */}
          <div className="bg-slate-800 rounded-lg shadow-sm border border-slate-700 p-6">
            <div className="flex items-center mb-4">
              <div className="flex-shrink-0">
                <TrendingUp className="h-8 w-8 text-purple-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-slate-400">Metrics Overview</p>
              </div>
            </div>
            <div className="space-y-2">
              {/* Excel Evaluation Metrics */}
              {statistics.evaluations.excel && statistics.evaluations.excel.metrics && (
                <>
                  {Object.entries(statistics.evaluations.excel.metrics).map(([metricName, metricData]) => (
                    <div key={metricName} className="flex justify-between items-center">
                      <span className="text-xs text-slate-300 capitalize">
                        {metricName.replace('_', ' ')}:
                      </span>
                      <div className="flex items-center space-x-2">
                        <span className="text-sm font-medium text-slate-100">
                          {formatScore(metricData.average_score)}
                        </span>
                        <span className="text-xs text-slate-400">
                          ({metricData.count})
                        </span>
                      </div>
                    </div>
                  ))}
                </>
              )}
              
              {/* Fallback to old format if new metrics structure not available */}
              {statistics.evaluations.excel && !statistics.evaluations.excel.metrics && (
                <>
                  {statistics.evaluations.excel.average_faithfulness > 0 && (
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-slate-300">Faithfulness:</span>
                      <span className="text-sm font-medium text-slate-100">
                        {formatScore(statistics.evaluations.excel.average_faithfulness)}
                      </span>
                    </div>
                  )}
                  {statistics.evaluations.excel.average_relevance > 0 && (
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-slate-300">Relevance:</span>
                      <span className="text-sm font-medium text-slate-100">
                        {formatScore(statistics.evaluations.excel.average_relevance)}
                      </span>
                    </div>
                  )}
                  {statistics.evaluations.excel.average_completeness > 0 && (
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-slate-300">Completeness:</span>
                      <span className="text-sm font-medium text-slate-100">
                        {formatScore(statistics.evaluations.excel.average_completeness)}
                      </span>
                    </div>
                  )}
                </>
              )}
              
              {/* Traditional Evaluation Metrics */}
              {statistics.evaluations.traditional && (
                <>
                  {statistics.evaluations.traditional.average_faithfulness > 0 && (
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-slate-300">Traditional Faithfulness:</span>
                      <span className="text-sm font-medium text-slate-100">
                        {formatScore(statistics.evaluations.traditional.average_faithfulness)}
                      </span>
                    </div>
                  )}
                  {statistics.evaluations.traditional.average_relevance > 0 && (
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-slate-300">Traditional Relevance:</span>
                      <span className="text-sm font-medium text-slate-100">
                        {formatScore(statistics.evaluations.traditional.average_relevance)}
                      </span>
                    </div>
                  )}
                  {statistics.evaluations.traditional.average_completeness > 0 && (
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-slate-300">Traditional Completeness:</span>
                      <span className="text-sm font-medium text-slate-100">
                        {formatScore(statistics.evaluations.traditional.average_completeness)}
                      </span>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Filters and Controls */}
      <div className="bg-slate-800 rounded-lg shadow-sm border border-slate-700 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-slate-100">Filters & Controls</h3>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="inline-flex items-center px-3 py-2 border border-slate-600 text-sm font-medium rounded-md text-slate-200 bg-slate-800 hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            <Filter className="h-4 w-4 mr-2" />
            {showFilters ? 'Hide' : 'Show'} Filters
          </button>
        </div>

        {showFilters && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-200 mb-1">Version</label>
              <select
                value={selectedVersion}
                onChange={(e) => setSelectedVersion(Number(e.target.value))}
                className="w-full px-3 py-2 border border-slate-600 rounded-md focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-purple-500"
              >
                <option value={0}>All Versions</option>
                {versions.map((version) => (
                  <option key={version.id} value={version.id}>
                    {version.version_name} {version.is_active && '(Active)'}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-200 mb-1">Score Range</label>
              <select
                value={scoreFilter}
                onChange={(e) => setScoreFilter(e.target.value)}
                className="w-full px-3 py-2 border border-slate-600 rounded-md focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-purple-500"
              >
                <option value="all">All Scores</option>
                <option value="excellent">Excellent (0.8-1.0)</option>
                <option value="good">Good (0.6-0.79)</option>
                <option value="fair">Fair (0.4-0.59)</option>
                <option value="poor">Poor (0.0-0.39)</option>
              </select>
            </div>

            <div className="flex items-end">
              <button
                onClick={loadData}
                className="w-full px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                Apply Filters
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Tab Content */}
      {activeTab === 'evaluations' ? (
        /* Excel Evaluation Results Tab */
        <div className="bg-slate-800 rounded-lg shadow-sm border border-slate-700">
          <div className="px-6 py-4 border-b border-slate-700">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium text-slate-100">
                Excel Evaluation Results ({filteredEvaluations.length})
              </h3>
              <div className="text-sm text-slate-400">
                Showing {filteredEvaluations.length} of {evaluations.length} Excel evaluations
              </div>
            </div>
          </div>
          
          {filteredEvaluations.length === 0 ? (
            <div className="text-center py-12">
              <Star className="h-12 w-12 mx-auto mb-3 text-slate-500" />
              <p className="text-slate-400 text-lg">No Excel evaluations found</p>
              <p className="text-slate-400 text-sm">
                {evaluations.length === 0 
                  ? 'No Excel evaluations exist for the selected organization, project, and version. Upload and run Excel evaluations to see results here.'
                  : 'Try adjusting your filters to see more evaluations'
                }
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-slate-700">
                <thead className="bg-slate-700">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">
                      Run ID
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">
                      Question
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">
                      Answer
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">
                      Metric Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">
                      Score
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">
                      Row
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-slate-800 divide-y divide-slate-700">
                  {filteredEvaluations.map((evaluation) => (
                    <tr key={evaluation.id} className="hover:bg-slate-700">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-slate-100">
                        {evaluation.evaluation_run_id || 'N/A'}
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-100 max-w-xs">
                        <div className="truncate" title={evaluation.question || evaluation.test_case}>
                          {evaluation.question || evaluation.test_case}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-100 max-w-xs">
                        <div className="truncate" title={evaluation.answer || 'N/A'}>
                          {evaluation.answer || 'N/A'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full text-purple-200 bg-purple-900/50">
                          {evaluation.metric_type?.replace('_', ' ').toUpperCase() || 'N/A'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center space-x-2">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getScoreColor(evaluation.overall_score)}`}>
                            {getScoreLabel(evaluation.overall_score)}
                          </span>
                          <span className={`text-sm font-medium ${getScoreTextColor(evaluation.overall_score)}`}>
                            {formatScore(evaluation.overall_score)}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-400">
                        {evaluation.row_number || 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-400">
                        {formatDate(evaluation.created_at)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button
                          onClick={() => handleViewDetails(evaluation)}
                          className="inline-flex items-center px-3 py-1 border border-slate-600 text-xs font-medium rounded text-slate-200 bg-slate-800 hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                        >
                          <Eye className="h-3 w-3 mr-1" />
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ) : (
        /* Test Executions Tab */
        <div className="bg-slate-800 rounded-lg shadow-sm border border-slate-700">
          <div className="px-6 py-4 border-b border-slate-700">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium text-slate-100">
                Test Executions ({testExecutions.length})
              </h3>
              <div className="text-sm text-slate-400">
                Showing {testExecutions.length} executions
              </div>
            </div>
          </div>
          
          {testExecutions.length === 0 ? (
            <div className="text-center py-12">
              <Play className="h-12 w-12 mx-auto mb-3 text-slate-500" />
              <p className="text-slate-400 text-lg">No test executions found</p>
              <p className="text-slate-400 text-sm">
                No test executions exist for the selected organization, project, and version. Run some tests to see execution details here.
              </p>
            </div>
          ) : (
            <div className="divide-y divide-slate-700">
              {testExecutions.map((execution) => (
                <div key={execution.id} className="px-6 py-4 hover:bg-slate-700">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <span className="inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full text-blue-200 bg-blue-900/50">
                          <Play className="h-3 w-3 mr-1" />
                          {execution.aut_version?.version_name || 'Unknown Version'}
                        </span>
                        
                        <span className="inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full text-green-200 bg-green-900/50">
                          <Clock className="h-3 w-3 mr-1" />
                          {execution.execution_time.toFixed(2)}s
                        </span>

                        {execution.tokens_used && (
                          <span className="inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full text-purple-200 bg-purple-900/50">
                            <FileText className="h-3 w-3 mr-1" />
                            {execution.tokens_used} tokens
                          </span>
                        )}
                      </div>
                      
                      <p className="text-slate-100 mb-3 font-medium">
                        {execution.test_case?.question_text || 'Test case not found'}
                      </p>
                      
                      <div className="mb-3">
                        <span className="text-slate-300 text-sm font-medium">Response:</span>
                        <p className="text-sm text-slate-200 mt-1 bg-slate-700 p-3 rounded-md max-h-32 overflow-y-auto">
                          {execution.customer_support_response}
                        </p>
                      </div>

                      {execution.context_used && execution.context_used.length > 0 && (
                        <div className="mb-3">
                          <span className="text-slate-300 text-sm font-medium">Context Used:</span>
                          <div className="text-sm text-slate-200 mt-1 bg-slate-700 p-3 rounded-md max-h-24 overflow-y-auto">
                            {execution.context_used.map((ctx, index) => (
                              <div key={index} className="mb-1">
                                <span className="font-medium">Source {index + 1}:</span> {typeof ctx === 'string' ? ctx : JSON.stringify(ctx)}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      <div className="flex items-center space-x-4 text-xs text-slate-400">
                        <span className="flex items-center">
                          <Calendar className="h-3 w-3 mr-1" />
                          {formatDate(execution.created_at)}
                        </span>
                        <span className="flex items-center">
                          <Database className="h-3 w-3 mr-1" />
                          Execution ID: {execution.id}
                        </span>
                        <span className="flex items-center">
                          <FileText className="h-3 w-3 mr-1" />
                          Test Case ID: {execution.test_case_id}
                        </span>
                      </div>
                    </div>
                    
                    <div className="ml-4">
                      <button
                        className="inline-flex items-center px-3 py-1 border border-slate-600 text-xs font-medium rounded text-slate-200 bg-slate-800 hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                      >
                        <Eye className="h-3 w-3 mr-1" />
                        View Details
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Evaluation Details Modal */}
      {showDetailsModal && selectedEvaluation && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-slate-800 border border-slate-700">
            <div className="mt-3">
              {/* Modal Header */}
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-slate-100">
                  Evaluation Details
                </h3>
                <button
                  onClick={closeDetailsModal}
                  className="text-slate-400 hover:text-slate-300"
                >
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Modal Content */}
              <div className="space-y-6">
                {/* Basic Information */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <h4 className="text-sm font-semibold text-slate-200 mb-2">Run ID</h4>
                    <p className="text-sm text-slate-100">{selectedEvaluation.evaluation_run_id || 'N/A'}</p>
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-slate-200 mb-2">Row Number</h4>
                    <p className="text-sm text-slate-100">{selectedEvaluation.row_number || 'N/A'}</p>
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-slate-200 mb-2">Metric Type</h4>
                    <span className="inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full text-purple-200 bg-purple-900/50">
                      {selectedEvaluation.metric_type?.replace('_', ' ').toUpperCase() || 'N/A'}
                    </span>
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-slate-200 mb-2">Score</h4>
                    <div className="flex items-center space-x-2">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getScoreColor(selectedEvaluation.overall_score)}`}>
                        {getScoreLabel(selectedEvaluation.overall_score)}
                      </span>
                      <span className={`text-sm font-medium ${getScoreTextColor(selectedEvaluation.overall_score)}`}>
                        {formatScore(selectedEvaluation.overall_score)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Question, Answer, and Context */}
                <div className="space-y-4">
                  <div>
                    <h4 className="text-sm font-semibold text-slate-200 mb-2 flex items-center">
                      <FileText className="h-4 w-4 mr-2" />
                      Question
                    </h4>
                    <p className="text-sm text-slate-100 bg-slate-700 p-3 rounded-md">
                      {selectedEvaluation.question || selectedEvaluation.test_case}
                    </p>
                  </div>

                  <div>
                    <h4 className="text-sm font-semibold text-slate-200 mb-2 flex items-center">
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Answer
                    </h4>
                    <p className="text-sm text-slate-100 bg-slate-700 p-3 rounded-md">
                      {selectedEvaluation.answer || 'N/A'}
                    </p>
                  </div>

                  <div>
                    <h4 className="text-sm font-semibold text-slate-200 mb-2 flex items-center">
                      <Database className="h-4 w-4 mr-2" />
                      Context
                    </h4>
                    <p className="text-sm text-slate-100 bg-slate-700 p-3 rounded-md">
                      {selectedEvaluation.context || 'N/A'}
                    </p>
                  </div>
                </div>

                {/* Reasoning and Detailed Feedback */}
                <div className="space-y-4">
                  {selectedEvaluation.reasoning && (
                    <div>
                      <h4 className="text-sm font-semibold text-slate-200 mb-2 flex items-center">
                        <TrendingUp className="h-4 w-4 mr-2" />
                        Reasoning
                      </h4>
                      <p className="text-sm text-slate-200 bg-slate-700 p-3 rounded-md">
                        {selectedEvaluation.reasoning}
                      </p>
                    </div>
                  )}

                  {selectedEvaluation.detailed_feedback && (
                    <div>
                      <h4 className="text-sm font-semibold text-slate-200 mb-2 flex items-center">
                        <BarChart3 className="h-4 w-4 mr-2" />
                        Detailed Feedback
                      </h4>
                      <p className="text-sm text-slate-200 bg-slate-700 p-3 rounded-md">
                        {selectedEvaluation.detailed_feedback}
                      </p>
                    </div>
                  )}

                  {selectedEvaluation.evaluation_prompt && (
                    <div>
                      <h4 className="text-sm font-semibold text-slate-200 mb-2 flex items-center">
                        <FileText className="h-4 w-4 mr-2" />
                        Evaluation Prompt
                      </h4>
                      <p className="text-sm text-slate-200 bg-slate-700 p-3 rounded-md max-h-32 overflow-y-auto">
                        {selectedEvaluation.evaluation_prompt}
                      </p>
                    </div>
                  )}
                </div>

                {/* Modal Footer */}
                <div className="flex justify-end space-x-3 pt-4 border-t border-slate-700">
                  <button
                    onClick={closeDetailsModal}
                    className="px-4 py-2 border border-slate-600 text-sm font-medium rounded-md text-slate-200 bg-slate-800 hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Results;
